import { useState, useEffect } from 'react';
import { apiService } from '../services/ApiService';

interface ClienteData {
  nome: string;
  email: string;
  telefone: string;
  senha: string;
}

interface LoginCredentials {
  email: string;
  senha: string;
}

interface Cliente {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  created_at: string;
}

const CLIENTE_ID_KEY = 'clienteId';
const CLIENTE_DATA_KEY = 'clienteData';

export const useCliente = () => {
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar cliente do localStorage ao montar e quando mudar
  useEffect(() => {
    const loadCliente = () => {
      const clienteId = localStorage.getItem(CLIENTE_ID_KEY);
      const clienteDataStr = localStorage.getItem(CLIENTE_DATA_KEY);
      
      if (clienteId && clienteDataStr) {
        try {
          const clienteData = JSON.parse(clienteDataStr);
          setCliente(clienteData);
        } catch (err) {
          console.error('Erro ao carregar dados do cliente:', err);
          // Limpar dados inválidos
          localStorage.removeItem(CLIENTE_ID_KEY);
          localStorage.removeItem(CLIENTE_DATA_KEY);
        }
      } else {
        setCliente(null);
      }
    };

    loadCliente();

    // Escutar mudanças no localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CLIENTE_ID_KEY || e.key === CLIENTE_DATA_KEY) {
        loadCliente();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Verificar mudanças periodicamente (para mesma aba)
    const interval = setInterval(loadCliente, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const cadastrarCliente = async (data: ClienteData): Promise<Cliente> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.cadastrarCliente(data);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Erro ao cadastrar cliente');
      }

      // O backend pode retornar { cliente: {...}, token: "..." } ou apenas { ... }
      const clienteData = response.data.cliente || response.data;
      const token = response.data.token;

      if (!clienteData || !clienteData.id) {
        throw new Error('Dados do cliente não encontrados na resposta');
      }

      const novoCliente: Cliente = {
        id: clienteData.id,
        nome: clienteData.nome,
        email: clienteData.email,
        telefone: clienteData.telefone,
        created_at: clienteData.created_at || new Date().toISOString()
      };

      // Salvar no localStorage
      localStorage.setItem(CLIENTE_ID_KEY, novoCliente.id);
      localStorage.setItem(CLIENTE_DATA_KEY, JSON.stringify(novoCliente));
      
      // Salvar token do cliente (se fornecido)
      if (token) {
        localStorage.setItem('clienteToken', token);
      }

      setCliente(novoCliente);
      
      // Disparar evento customizado para atualizar componentes que dependem do cliente
      window.dispatchEvent(new CustomEvent('clienteUpdated', { detail: novoCliente }));
      
      return novoCliente;
    } catch (err: any) {
      const errorMessage = err?.message || 'Erro ao cadastrar cliente';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginCliente = async (credentials: LoginCredentials): Promise<Cliente> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.loginCliente(credentials);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Erro ao fazer login');
      }

      // O backend retorna { cliente: {...}, token: "..." }
      // Verificar se a resposta tem a estrutura esperada
      const clienteData = response.data.cliente || response.data;
      const token = response.data.token;

      if (!clienteData || !clienteData.id) {
        throw new Error('Dados do cliente não encontrados na resposta');
      }

      const clienteLogado: Cliente = {
        id: clienteData.id,
        nome: clienteData.nome,
        email: clienteData.email,
        telefone: clienteData.telefone,
        created_at: clienteData.created_at || new Date().toISOString()
      };

      // Salvar no localStorage
      localStorage.setItem(CLIENTE_ID_KEY, clienteLogado.id);
      localStorage.setItem(CLIENTE_DATA_KEY, JSON.stringify(clienteLogado));
      
      // Salvar token do cliente (se fornecido)
      if (token) {
        localStorage.setItem('clienteToken', token);
      }

      setCliente(clienteLogado);
      
      // Disparar evento customizado para atualizar componentes que dependem do cliente
      window.dispatchEvent(new CustomEvent('clienteUpdated', { detail: clienteLogado }));
      
      return clienteLogado;
    } catch (err: any) {
      const errorMessage = err?.message || 'Erro ao fazer login';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getClienteId = (): string | null => {
    return cliente?.id || localStorage.getItem(CLIENTE_ID_KEY);
  };

  const isClienteCadastrado = (): boolean => {
    return cliente !== null || localStorage.getItem(CLIENTE_ID_KEY) !== null;
  };

  const logout = () => {
    localStorage.removeItem(CLIENTE_ID_KEY);
    localStorage.removeItem(CLIENTE_DATA_KEY);
    localStorage.removeItem('clienteToken');
    setCliente(null);
  };

  return {
    cliente,
    loading,
    error,
    cadastrarCliente,
    loginCliente,
    getClienteId,
    isClienteCadastrado,
    logout
  };
};

